import { createTheme } from '@mui/material/styles';

const colors = {
  bg0: '#060816',
  bg1: '#0B1020',
  bg2: '#10182B',
  surface1: '#111827',
  surface2: '#172033',
  surface3: '#1C2740',
  primary: '#1E90FF',
  primary2: '#3AA0FF',
  secondary: '#8B5CF6',
  secondary2: '#A855F7',
  accent: '#00D4FF',
  success: '#22C55E',
  warning: '#F59E0B',
  error: '#EF4444'
};

const grey = {
  50: '#F7F8FA',
  100: '#F2F4F8',
  200: '#E5E7EB',
  300: '#CBD5E1',
  400: '#94A3B8',
  500: '#64748B',
  600: '#475569',
  700: '#334155',
  800: '#1F2937',
  900: '#0B1220'
};

export default function buildTheme(mode = 'light') {
  const isDark = mode === 'dark';
  const bgDefault = isDark ? colors.bg0 : '#F7F8FA';
  const paper = isDark ? colors.surface1 : '#FFFFFF';
  const divider = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(15,23,42,0.12)';
  const textPrimary = isDark ? '#EAF0FF' : '#0B1220';
  const textSecondary = isDark ? 'rgba(234,240,255,0.68)' : 'rgba(15,23,42,0.68)';

  return createTheme({
    palette: {
      mode,
      primary: { main: colors.primary },
      secondary: { main: colors.secondary },
      success: { main: colors.success },
      warning: { main: colors.warning },
      error: { main: colors.error },
      info: { main: colors.primary2 },
      grey,
      background: {
        default: bgDefault,
        paper
      },
      divider,
      text: { primary: textPrimary, secondary: textSecondary }
    },
    shape: { borderRadius: 14 },
    spacing: 8,
    typography: {
      fontFamily: [
        'Inter',
        '"Plus Jakarta Sans"',
        '-apple-system',
        'BlinkMacSystemFont',
        '"Segoe UI"',
        'Roboto',
        '"Helvetica Neue"',
        'Arial',
        '"Noto Sans"',
        'sans-serif',
      ].join(','),
      h1: { fontWeight: 800, letterSpacing: '-0.02em' },
      h2: { fontWeight: 800, letterSpacing: '-0.02em' },
      h3: { fontWeight: 750, letterSpacing: '-0.02em' },
      h4: { fontWeight: 750, letterSpacing: '-0.015em' },
      h5: { fontWeight: 750, letterSpacing: '-0.01em' },
      h6: { fontWeight: 700, letterSpacing: '-0.01em' },
      button: { textTransform: 'none', fontWeight: 650, letterSpacing: '-0.005em' }
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: bgDefault,
            backgroundImage: isDark
              ? 'radial-gradient(1100px 700px at 20% 0%, rgba(30,144,255,0.14), transparent 60%), radial-gradient(900px 600px at 85% 10%, rgba(139,92,246,0.12), transparent 55%), linear-gradient(180deg, #060816, #0B1020 38%, #10182B)'
              : 'radial-gradient(900px 600px at 10% 0%, rgba(30,144,255,0.10), transparent 55%), radial-gradient(900px 600px at 90% 0%, rgba(139,92,246,0.10), transparent 55%), linear-gradient(180deg, #F8FAFF, #F7F8FA)',
            color: textPrimary
          }
        }
      },
      MuiButton: {
        defaultProps: { variant: 'contained', disableElevation: true },
        styleOverrides: {
          root: {
            borderRadius: 12,
            paddingInline: 14,
            paddingBlock: 10,
            transition: 'transform 180ms ease, background-color 180ms ease, border-color 180ms ease'
          },
          containedPrimary: {
            backgroundImage: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.primary2} 60%, ${colors.accent} 140%)`,
            boxShadow: isDark ? '0 8px 26px rgba(30,144,255,0.18)' : '0 10px 28px rgba(30,144,255,0.16)'
          },
          containedSecondary: {
            backgroundImage: `linear-gradient(135deg, ${colors.secondary} 0%, ${colors.secondary2} 65%)`,
            boxShadow: isDark ? '0 8px 26px rgba(139,92,246,0.18)' : '0 10px 28px rgba(139,92,246,0.14)'
          },
          outlined: {
            borderColor: divider,
            backgroundColor: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(15,23,42,0.02)'
          }
        }
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 650,
            borderRadius: 999,
            border: `1px solid ${divider}`,
            backgroundColor: isDark ? 'rgba(255,255,255,0.04)' : 'rgba(15,23,42,0.04)'
          }
        }
      },
      MuiTableHead: {
        styleOverrides: {
          root: { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.03)' }
        }
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            borderRadius: 16,
            backgroundImage: isDark
              ? 'linear-gradient(180deg, rgba(255,255,255,0.06), rgba(255,255,255,0.02))'
              : 'linear-gradient(180deg, rgba(15,23,42,0.03), rgba(15,23,42,0.01))',
            border: `1px solid ${divider}`
          }
        }
      },
      MuiTextField: {
        defaultProps: { size: 'small', variant: 'outlined' }
      },
      MuiSelect: {
        defaultProps: { size: 'small' }
      },
      MuiFormControl: {
        defaultProps: { size: 'small' }
      },
      MuiOutlinedInput: {
        styleOverrides: {
          root: {
            borderRadius: 12,
            backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(15,23,42,0.02)',
            transition: 'background-color 180ms ease, border-color 180ms ease'
          },
          notchedOutline: {
            borderColor: divider
          }
        }
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            backdropFilter: 'blur(14px)'
          }
        }
      },
      MuiSkeleton: {
        defaultProps: { animation: 'pulse' }
      }
    }
  });
}
